import { XMarkIcon } from '@heroicons/react/24/outline';
import type { TimelineEvent } from '../../types';
import { useEscapeKey } from '../../../../../hooks/useEscapeKey';

interface NewVersionFromPreviousModalProps {
    previousEvent: TimelineEvent;
    onStartOver: () => void;
    onFromPrevious: () => void;
    onCancel: () => void;
}

/**
 * Asks the user how to seed the next version when a previous submitted
 * version already exists. Both options save back to the same group and
 * bump the version number; the only difference is whether the previous
 * payload pre-fills the form.
 */
export function NewVersionFromPreviousModal({
    previousEvent,
    onStartOver,
    onFromPrevious,
    onCancel
}: NewVersionFromPreviousModalProps) {
    const nextVersion = previousEvent.version + 1;

    useEscapeKey(onCancel, true);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[480px] p-6 relative">
                <div className="flex justify-between items-start mb-3">
                    <h3 className="text-base font-semibold text-gray-900">Create v{nextVersion}</h3>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                    A previous version (v{previousEvent.version}) already exists. How would you like
                    to start the new version?
                </p>
                <div className="flex justify-end items-center gap-3">
                    <button
                        onClick={onStartOver}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Start over
                    </button>
                    <button
                        onClick={onFromPrevious}
                        className="px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Start from previous
                    </button>
                </div>
            </div>
        </div>
    );
}
